import { ChangeEvent, useCallback } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { PolicyForm } from '../policy-dialog';

import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';

interface PolicyEmployeeIdTextFieldProps {
  watch: UseFormWatch<PolicyForm>;
  setValue: UseFormSetValue<PolicyForm>;
  disabled: boolean;
  fieldName: `actionData.${number}.userModelTemplate.externalIds`;
}

export default function PolicyEmployeeIdTextField({
  watch,
  setValue,
  disabled,
  fieldName,
}: PolicyEmployeeIdTextFieldProps) {
  const externalIds = watch(fieldName);

  const handleOrgIdChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const organizationIds =
        externalIds?.filter((id) => id.type === 'organization') || [];
      const hasOrganizationIds = organizationIds.length > 0;

      if (hasOrganizationIds) {
        if (e.target.value === '' && organizationIds.length === 1) {
          setValue(
            fieldName,
            externalIds?.filter((id) => id.type !== 'organization'),
            { shouldDirty: true }
          );
          return;
        } else {
          let foundOrgId = false;

          setValue(
            fieldName,
            externalIds?.map((id) => {
              if (id.type === 'organization' && !foundOrgId) {
                foundOrgId = true;
                return { ...id, value: e.target.value };
              }
              return id;
            }),
            { shouldDirty: true }
          );
        }
      } else {
        setValue(
          fieldName,
          [
            ...(externalIds || []),
            {
              type: 'organization',
              customType: '',
              value: e.target.value,
            },
          ],
          { shouldDirty: true }
        );
      }
    },
    [externalIds, setValue, fieldName]
  );

  return (
    <Tooltip
      arrow
      title={
        disabled
          ? `You need the "Edit External Ids" permission to edit this field.`
          : ''
      }
    >
      <span>
        <TextField
          value={
            externalIds?.find((id) => id.type === 'organization')?.value || ''
          }
          onChange={(e) => handleOrgIdChange(e)}
          autoComplete="off"
          label="Employee ID"
          sx={{ minWidth: 400 }}
          disabled={disabled}
        />
      </span>
    </Tooltip>
  );
}
